import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export type ForkHireAnalyticsSummary = {
  totalApplications: number;
  scored: number;
  interviewed: number;
  hired: number;
  rejected: number;
};

export type ForkHireAnalyticsPerJob = {
  jobId: string;
  title: string;
  department: string | null;
  status: string;
  applications: number;
  scored: number;
  interviewed: number;
  hired: number;
  rejected: number;
};

export type ForkHireAnalytics = {
  summary: ForkHireAnalyticsSummary;
  perJob: ForkHireAnalyticsPerJob[];
};

type ForkHireAnalyticsRpcClient = {
  rpc: (fn: 'get_forkhire_analytics') => Promise<{ data: unknown; error: unknown }>;
};

const EMPTY_ANALYTICS: ForkHireAnalytics = {
  summary: {
    totalApplications: 0,
    scored: 0,
    interviewed: 0,
    hired: 0,
    rejected: 0,
  },
  perJob: [],
};

function asNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function normalizeForkHireAnalyticsPayload(data: unknown): ForkHireAnalytics {
  if (!data || typeof data !== 'object') return EMPTY_ANALYTICS;

  const payload = data as {
    summary?: Partial<ForkHireAnalyticsSummary>;
    perJob?: Partial<ForkHireAnalyticsPerJob>[];
  };

  return {
    summary: {
      totalApplications: asNumber(payload.summary?.totalApplications),
      scored: asNumber(payload.summary?.scored),
      interviewed: asNumber(payload.summary?.interviewed),
      hired: asNumber(payload.summary?.hired),
      rejected: asNumber(payload.summary?.rejected),
    },
    perJob: Array.isArray(payload.perJob)
      ? payload.perJob.map((job) => ({
          jobId: String(job.jobId ?? ''),
          title: String(job.title ?? ''),
          department: typeof job.department === 'string' ? job.department : null,
          status: String(job.status ?? ''),
          applications: asNumber(job.applications),
          scored: asNumber(job.scored),
          interviewed: asNumber(job.interviewed),
          hired: asNumber(job.hired),
          rejected: asNumber(job.rejected),
        })).filter((job) => job.jobId && job.title)
      : [],
  };
}

async function fetchForkHireAnalytics() {
  const { data, error } = await (supabase as unknown as ForkHireAnalyticsRpcClient).rpc('get_forkhire_analytics');
  if (error) throw error;
  return normalizeForkHireAnalyticsPayload(data);
}

export function useForkHireAnalytics() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery({
    queryKey: ['hire-analytics', orgId],
    queryFn: fetchForkHireAnalytics,
    enabled: !!orgId,
    refetchOnWindowFocus: true,
  });
}
